'use client';

import { API_ROUTES, HTTP_METHODS, useApiMutation } from '@shared/api-utils';
import { useApiValidation, type ValidationResult } from '@shared/hooks';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { omitStateField } from '../utils/payloadTransformer';
import { inferRegistrationFailureKind, setRegistrationFailureKind } from '../utils/registrationFailureKind';

type UsePlayerValidationOptions<TFormData extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFormData>;
  fieldMapping?: Record<string, string>;
  fieldMessages?: Record<string, string>;
  transformPayload?: (formData: TFormData) => Record<string, unknown>;
};

/**
 * Shared hook for player validation across registration steps.
 * Encapsulates the common API validation setup used by all registration steps.
 */
const usePlayerValidation = <TFormData extends FieldValues = FieldValues>({
  form,
  fieldMapping = {},
  fieldMessages = {},
  transformPayload = formData => formData as Record<string, unknown>,
}: UsePlayerValidationOptions<TFormData>) => {
  // API mutation for player validation (shared across all registration steps)
  // 'X-Validation-Type': 'business' instructs the monolith to return HTTP 200 instead of 422 (CHR-2439)
  const validatePlayerMutation = useApiMutation<unknown, Record<string, unknown>>(API_ROUTES.REGISTER.PLAYER_VALIDATION, HTTP_METHODS.POST, {
    requestOptions: {
      headers: {
        Accept: 'application/json',
        'X-Validation-Type': 'business',
      },
    },
  });

  // Reusable API validation hook
  const { validateWithApi, isValidating } = useApiValidation<unknown, Record<string, unknown>, TFormData>({
    mutation: validatePlayerMutation,
    form,
    fieldMapping,
    fieldMessages,
    transformPayload: formData => omitStateField(transformPayload(formData)),
  });

  return {
    validateWithApi,
    isValidating,
  };
};

/**
 * Hook for handling server error routing in registration steps.
 * Provides a reusable function to check validation errors and route to error step.
 */
const useValidationErrorHandler = () => {
  const { setStatus, skipToStep } = useMultistepForm();

  /**
   * Handles server error routing for validation failures.
   * @param statusCode - HTTP status code from validation response
   * @param caughtError - Original error for classifying fraud / SEON restrictions vs system failures
   * @returns boolean - true if error was handled (server error), false if validation error
   */
  const handleError = (statusCode: number | undefined, caughtError?: unknown): boolean => {
    // Check if the validation failed due to a server error (anything other than 422)
    if (statusCode && statusCode !== 422) {
      setRegistrationFailureKind(inferRegistrationFailureKind(caughtError, statusCode));
      // Server error - show error step
      setStatus('error');
      skipToStep('error');
      return true; // Indicates error was handled
    }

    // Network failures may have no HTTP status; still route to the system error screen
    if (!statusCode && caughtError) {
      setRegistrationFailureKind(inferRegistrationFailureKind(caughtError, statusCode));
      setStatus('error');
      skipToStep('error');
      return true;
    }

    return false; // Indicates it was a validation error, stay on current step
  };

  return { handleError };
};

/**
 * Enhanced hook for step validation with error status tracking.
 * Provides validation with status code for manual error handling.
 */
const useStepValidation = <TFormData extends FieldValues = FieldValues>({
  form,
  fieldMapping = {},
  fieldMessages = {},
  transformPayload = formData => formData as Record<string, unknown>,
}: UsePlayerValidationOptions<TFormData>) => {
  const { validateWithApi, isValidating } = usePlayerValidation({
    form,
    fieldMapping,
    fieldMessages,
    transformPayload,
  });

  /**
   * Validates data and returns result with status code for error handling.
   * @param variables - Variables to pass to the mutation
   * @param additionalPayload - Additional data to include in validation
   * @param onSuccess - Optional callback for successful validation (receives API success body)
   * @returns Promise<ValidationResult> - validation result with status code and optional caught error
   */
  const validateWithStatus = async (
    variables: Record<string, unknown> = {},
    additionalPayload?: Partial<Record<string, unknown>>,
    onSuccess?: (data: unknown) => void
  ): Promise<ValidationResult> => {
    return await validateWithApi(variables, additionalPayload, onSuccess);
  };

  return {
    validateWithStatus,
    isValidating,
  };
};

export { usePlayerValidation, useStepValidation, useValidationErrorHandler };
